import window from 'window-or-global';
import AJS from 'ajs_DO_NOT_USE';
import { trigger } from '@confluence-classic/confluence-event_DO_NOT_USE';
import { getRte } from '../../facades/editor';
import { CONTEXT_PATH } from '@confluence-classic/confluence-urls';
import { getContentId } from './commons';
import NodeUtils from './tinymce-node-utils';
// TODO: remove jquery from here and from webpack config once CONFDEV-52616 is done.
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';

//TODO: refactor because this function is duplicated in macro-browser.js (we avoid circular dependency)
function isMacroWithBody(node) {
	return $(node).hasClass('wysiwyg-macro');
}

/**
 * Places the editor cursor inside a bodied macro placeholder.
 */
function focusMacroNode(macroNode) {
	const ed = window.tinymce.activeEditor;
	const doc = ed.getDoc();
	const range = ed.selection.getRng(true);

	if (isMacroWithBody(macroNode)) {
		// add a BR to allow the cursor to be positioned in the placeholder
		!window.tinymce.isIE &&
			$('p, pre', macroNode).each((i, el) => {
				if (el.childNodes.length === 0) {
					el.appendChild(doc.createElement('br'));
				}
			});

		const nodeToFocus = ed.dom.select('.wysiwyg-macro-body', macroNode)[0].firstChild;
		if (nodeToFocus) {
			range.setStart(nodeToFocus, 0);
			range.setEnd(nodeToFocus, 0);
		} else {
			if (process.env.NODE_ENV !== 'production') {
				console.debug("focusMacroNode: couldn't find anything to focus, cap'n!");
			}
		}
	} else {
		range.setStartAfter(macroNode);
		range.setEndAfter(macroNode);
	}

	ed.selection.setRng(range);
}

function handleMacroParameterExceptions(macro) {
	if (macro.macro?.name === 'anchor') {
		// Stripping out % because it makes an invalid URL
		macro.macro.defaultParameterValue = macro.macro.defaultParameterValue.replace(/%+/g, '');
		// Stripping out any single or double quotes for security (VULN-539679)
		macro.macro.defaultParameterValue = macro.macro.defaultParameterValue.replace(/["']+/g, '');
	}
}

function insertMacro(macro, nodeToReplace) {
	let request;

	if (macro.macro && macro.contentId) {
		handleMacroParameterExceptions(macro);

		// if macro data
		request = {
			type: 'POST',
			url: `${CONTEXT_PATH}/rest/tinymce/1/macro/placeholder`,
			contentType: 'application/json; charset=utf-8',
			data: JSON.stringify(macro),
			dataType: 'text',
		};
	} else if (macro.url && macro.type) {
		// if jQuery AJAX request
		request = macro;
	} else {
		throw new Error(`illegal argument received: ${macro}`);
	}

	/**
	 * insertMacro consists of one or more asynchronous operations. This purpose of this $deferred object is to
	 * allow insertMacro() and clients of insertMacro() to queue up callbacks to run after the asynchronous operation is complete.
	 */
	const $deferred = $.Deferred();
	const ed = window.tinymce.activeEditor;
	let tempNode;

	if (!nodeToReplace) {
		// Insert a temporary node that will be used to hold the fort while we wait for the server
		const placeholderId = `macro-${new Date().getTime()}`;
		tempNode = ed.dom.create('span', { id: placeholderId });

		ed.selection.setNode(tempNode);
		/**
		 * window.tinymce.dom.Selection.setNode() takes the node passed in, makes a copy of it internally
		 * and then inserts into the editor's DOM. It does not return a reference to node it creates :/
		 * hence we have to manually get a reference to it here.
		 */
		nodeToReplace = tempNode = ed.dom.select(`#${placeholderId}`)[0];

		$deferred.fail(() => {
			$(tempNode).remove();
		});
	}

	/**
	 * Mandate a sensible timeout. We don't want the editor buttons disabled for an unreasonable amount of time.
	 * Better to re-enable them and fail the macro insertion and allow the user to save any changes they have made.
	 */
	$.extend(request, { timeout: this.DEFAULT_INSERT_MACRO_TIMEOUT });

	if (process.env.NODE_ENV !== 'production') {
		console.debug(`Insert macro timeout = ${request.timeout}`);
	}

	AJS.Editor.UI.setButtonsState(false);

	$.ajax(request)
		.done(function (macroMarkup) {
			if (macroMarkup[0] !== '<') {
				//change the markup into a text node so that it can be handled by jQuery
				macroMarkup = document.createTextNode(macroMarkup);
			}

			/**
			 * Check if the node is still there before going any further.
			 * The user could have deleted the node while the xhr request was in progress.
			 * If that's the case and we manipulate a deleted node, we could expect some nasty
			 * behaviour like CONFDEV-21949
			 */
			function validNodeAttached(node) {
				return node && node.parentNode;
			}

			if (!validNodeAttached(nodeToReplace)) {
				if (process.env.NODE_ENV !== 'production') {
					console.error('The node to be replaced has been already deleted from the document');
				}
				$deferred.rejectWith(this, arguments);
			} else {
				NodeUtils.updateNode(nodeToReplace, macroMarkup)
					.done((newNode) => {
						$deferred.resolve(newNode, macroMarkup);
					})
					.fail($deferred.reject); // updateNode does not reject at this point in time
			}
		})
		.fail(function (jqXHR, textStatus, errorThrown) {
			if (process.env.NODE_ENV !== 'production') {
				console.error(`Macro placeholder request failed ${textStatus} : '${errorThrown}'`);
			}
			$deferred.rejectWith(this, arguments);
		});

	/**
	 * All operations that are to happen after all asynchronous operations are complete should be queued up here.
	 */
	$deferred
		.done((newNode, newNodeMarkup) => {
			AJS.Editor.UI.setButtonsState(true);

			/**
			 * Split out body macro placeholder if it is inside a heading or paragraph (tables are not allowed inside these tags according to the html spec)
			 */
			if (isMacroWithBody(newNode) && /P|H\d/.test(newNode.parentNode.nodeName)) {
				ed.dom.split(newNode.parentNode, newNode);
			}

			focusMacroNode(newNode);

			/**
			 * One reason to call this here is to ensure the toolbar buttons are disabled when inside a plain text macro placeholder
			 */
			ed.nodeChanged();

			// ATLASSIAN - CONFDEV-3749 - make sure new element is visible
			// ATLASSIAN - CONFDEV-6400 - Make sure that the whole macro is visible
			getRte().showElement(newNode);

			ed.selection.onSetContent.dispatch(ed.selection, {
				content: newNodeMarkup,
				format: 'html',
			});

			window.tinymce.activeEditor.undoManager.add();

			// This event is used with AJS.trace to allow WD tests to wait until the macro placeholder
			// has been added to the Editor.
			trigger('macro-browser.macro-inserted');
		})
		.fail(() => {
			AJS.Editor.UI.setButtonsState(true);
		});

	return $deferred.promise();
}

const MacroUtils = {
	DEFAULT_INSERT_MACRO_TIMEOUT: 5000,

	/**
	 * Inserts a macro into the editor asynchronously. The specified macro could be either:
	 *
	 * (1) macro data (consisting of macro and a contentId)
	 * (2) an jQuery ajax request that returns macro markup in editor format
	 *
	 * @param macro macro data or a macro AJAX request
	 * @param nodeToReplace (optional) if specified, this node will be replaced with the macro being inserted
	 *
	 * @return a jQuery promise object to allow callbacks to be queued up that will be invoked when this asynchronous operation is complete.
	 */
	insertMacro,
	handleMacroParameterExceptions,
	isInMacro(node) {
		const $node = node.jquery ? node : $(node);
		return $node.closest('.wysiwyg-macro').length > 0;
	},

	updateMacro(updatedParameters, updatedMacroBody, macroName, existingMacroNode) {
		const macroRenderRequest = {
			contentId: getContentId(),
			macro: {
				name: macroName,
				params: updatedParameters,
				body: updatedMacroBody || '',
			},
		};

		return insertMacro(macroRenderRequest, existingMacroNode);
	},
};

export default MacroUtils;

export function exportMacroUtilsAsGlobal() {
	window.tinymce.confluence = window.tinymce.confluence || {};
	window.tinymce.confluence.MacroUtils = MacroUtils;
}
